/**
 * AppSidebar Component
 * Persistent navigation sidebar with collapsible functionality
 * Bloomberg Terminal-inspired professional sidebar
 * Mobile-responsive with overlay drawer on small screens
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  Scale,
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
  Palette,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  matchPaths?: string[];
}

interface AppSidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/dashboard',
    matchPaths: ['/dashboard', '/project'],
  },
  {
    label: 'Portfolio',
    icon: Briefcase,
    href: '/portfolio',
    matchPaths: ['/portfolio'],
  },
  {
    label: 'Knowledge Base',
    icon: FileText,
    href: '/documents',
    matchPaths: ['/documents'],
  },
  {
    label: 'Graphics Studio',
    icon: Palette,
    href: '/graphics',
    matchPaths: ['/graphics'],
  },
  {
    label: 'Legal',
    icon: Scale,
    href: '/legal',
    matchPaths: ['/legal'],
  },
  {
    label: 'Settings',
    icon: Settings,
    href: '/settings',
    matchPaths: ['/settings'],
  },
];

const SIDEBAR_STORAGE_KEY = 'corbel_sidebar_collapsed';

export const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed: controlledCollapsed,
  onCollapsedChange,
}) => {
  const location = useLocation();
  const { user, userProfile, isPlatformAdmin } = useAuth();

  // Build nav items - conditionally include Admin before Settings
  const navItems = useMemo(() => {
    const items = [...NAV_ITEMS];
    if (isPlatformAdmin) {
      // Insert Admin before Settings (last item)
      const settingsIndex = items.findIndex(item => item.label === 'Settings');
      const adminItem: NavItem = {
        label: 'Admin',
        icon: Shield,
        href: '/admin',
        matchPaths: ['/admin'],
      };
      if (settingsIndex >= 0) {
        items.splice(settingsIndex, 0, adminItem);
      } else {
        items.push(adminItem);
      }
    }
    return items;
  }, [isPlatformAdmin]);

  const companyName = userProfile?.companyName?.trim() || '';
  const companyInitial = companyName ? companyName.charAt(0).toUpperCase() : '';
  const userAvatarUrl = userProfile?.avatarUrl || userProfile?.photoURL || user?.photoURL || null;
  const userDisplayName = userProfile?.displayName || user?.displayName || user?.email || 'User';

  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    if (window.innerWidth < 1024) {
      return true;
    }

    // Load collapsed state from localStorage (default to true = collapsed)
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored ? JSON.parse(stored) : true; // ✅ Default to collapsed for better UX
    } catch {
      return true;
    }
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const mobileOpenerRef = useRef<HTMLElement | null>(null);
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const collapsedRef = useRef(collapsed);
  const onCollapsedChangeRef = useRef(onCollapsedChange);
  useLayoutEffect(() => {
    collapsedRef.current = collapsed;
    onCollapsedChangeRef.current = onCollapsedChange;
  }, [collapsed, onCollapsedChange]);

  const setCollapsed = useCallback((next: boolean | ((previous: boolean) => boolean)) => {
    const nextValue = typeof next === 'function' ? next(collapsedRef.current) : next;
    collapsedRef.current = nextValue;
    setInternalCollapsed(nextValue);
    onCollapsedChangeRef.current?.(nextValue);
  }, []);

  // Save collapsed state to localStorage
  useEffect(() => {
    if (window.innerWidth < 1024) {
      return;
    }

    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(collapsed));
    } catch (error) {
      console.error('Failed to save sidebar state:', error);
    }
  }, [collapsed]);

  useEffect(() => {
    let wasMobileViewport = window.innerWidth < 1024;
    const handleViewportChange = () => {
      const mobileViewport = window.innerWidth < 1024;
      setIsMobile(mobileViewport);
      if (mobileViewport && !wasMobileViewport) {
        setCollapsed(true);
      }
      wasMobileViewport = mobileViewport;
    };

    window.addEventListener('resize', handleViewportChange);
    return () => window.removeEventListener('resize', handleViewportChange);
  }, [setCollapsed]);

  // Listen for toggle events from hamburger menu
  useEffect(() => {
    const handleToggle = () => {
      if (isMobile && collapsed) {
        mobileOpenerRef.current = document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      }
      setCollapsed((prev: boolean) => !prev);
    };

    window.addEventListener('toggleSidebar', handleToggle);
    return () => window.removeEventListener('toggleSidebar', handleToggle);
  }, [collapsed, isMobile, setCollapsed]);

  /**
   * Check if current route matches nav item
   */
  const isActive = (item: NavItem): boolean => {
    if (!item.matchPaths) return false;
    return item.matchPaths.some((path) => location.pathname.startsWith(path));
  };

  /**
   * Toggle sidebar collapsed state
   */
  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const renderSidebarContent = (compact: boolean, mobile = false) => (
    <>
      <div className="flex h-16 items-center justify-between border-b border-border px-4">
        {compact ? (
          userAvatarUrl ? (
            <Link to="/settings?tab=profile" className="mx-auto rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background" title="Profile avatar">
              <UserAvatar
                src={userAvatarUrl}
                name={userDisplayName}
                email={user?.email}
                className="h-8 w-8 shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]"
                fallbackClassName="text-xs"
              />
            </Link>
          ) : companyName ? (
            <div className="mx-auto flex h-8 w-8 items-center justify-center bg-primary shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]">
              <span className="font-display text-lg leading-none text-primary-foreground">{companyInitial}</span>
            </div>
          ) : (
            <Link to="/settings?tab=organization" className="mx-auto flex h-8 w-8 items-center justify-center border border-dashed border-muted-foreground/30 bg-muted transition-colors hover:border-primary/50" title="Set your company name in Settings">
              <span className="text-lg text-muted-foreground">?</span>
            </Link>
          )
        ) : (
          <div className="flex min-w-0 items-center gap-2 pr-8">
            {userAvatarUrl ? (
              <Link to="/settings?tab=profile" className="flex min-w-0 items-center gap-2 text-foreground transition-colors hover:text-primary" title="Open profile">
                <UserAvatar
                  src={userAvatarUrl}
                  name={userDisplayName}
                  email={user?.email}
                  className="h-8 w-8 flex-shrink-0 shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]"
                  fallbackClassName="text-xs"
                />
                <span className="truncate font-display text-base leading-none tracking-[-0.03em]">{companyName || userDisplayName}</span>
              </Link>
            ) : companyName ? (
              <>
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center bg-primary shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]">
                  <span className="font-display text-lg leading-none text-primary-foreground">{companyInitial}</span>
                </div>
                <span className="truncate font-display text-base leading-none tracking-[-0.03em] text-foreground">{companyName}</span>
              </>
            ) : (
              <Link to="/settings?tab=organization" className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground" title="Set your company name">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center border border-dashed border-muted-foreground/30 bg-muted">
                  <span className="text-lg text-muted-foreground">?</span>
                </div>
                <span className="text-sm italic">Set company name</span>
              </Link>
            )}
          </div>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4" aria-label="Primary navigation">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          const linkContent = (
            <Link
              to={item.href}
              onClick={() => mobile && setCollapsed(true)}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 transition-all duration-150 ease',
                'hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'focus-visible:outline-2 focus-visible:outline-primary/50',
                active
                  ? 'bg-primary font-medium text-primary-foreground shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]'
                  : 'text-muted-foreground hover:text-foreground active:scale-[0.98]',
                compact && 'justify-center'
              )}
            >
              <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              {!compact && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          );

          return compact ? (
            <Tooltip key={item.label}>
              <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
              <TooltipContent side="right" className="border-border bg-accent text-foreground">
                <p>{item.label}</p>
              </TooltipContent>
            </Tooltip>
          ) : <div key={item.label}>{linkContent}</div>;
        })}
      </nav>

      {!mobile && (
        <div className="border-t border-border p-2">
          <Button
            onClick={toggleSidebar}
            variant="ghost"
            size="sm"
            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!compact}
            className={cn(
              'w-full text-muted-foreground hover:bg-accent hover:text-foreground',
              compact ? 'justify-center px-0' : 'justify-start px-3'
            )}
          >
            {compact ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="mr-2 h-4 w-4" /><span className="text-xs">Collapse</span></>}
          </Button>
        </div>
      )}
    </>
  );

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        role="navigation"
        aria-label="Main navigation"
        className={cn(
          'fixed left-0 top-0 z-40 hidden h-screen flex-col border-r border-border bg-card transition-[width] duration-300 ease lg:flex',
          collapsed ? 'w-16' : 'w-60'
        )}
      >
        {renderSidebarContent(collapsed)}
      </aside>

      <Sheet open={isMobile && !collapsed} onOpenChange={(open) => setCollapsed(!open)}>
        <SheetContent
          side="left"
          className="flex w-72 max-w-[85vw] flex-col gap-0 border-border bg-card p-0 lg:hidden"
          onCloseAutoFocus={(event) => {
            const opener = mobileOpenerRef.current;
            mobileOpenerRef.current = null;
            if (opener?.isConnected && opener.getClientRects().length > 0) {
              event.preventDefault();
              opener.focus();
            }
          }}
        >
          <SheetTitle className="sr-only">Main navigation</SheetTitle>
          <SheetDescription className="sr-only">Navigate ReturnCatalyst</SheetDescription>
          {renderSidebarContent(false, true)}
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
};
